import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import useForm from 'components/hooks/useForm';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ErrorMessage from 'components/application/ErrorMessage';
import ModalBox from 'components/application/ModalBox';
import TextField from 'components/application/TextField';

import { useBreadBoard } from 'components/contexts/Toaster';

export default function RenameFolderModal({
  folderId,
  isConfirmDisabled = false,
  onClose,
  onConfirm,
  onInputFocus,
  requestError,
}) {
  const breadBoard = useBreadBoard();

  const [currentFolder, setCurrentFolder, handleCurrentFolderInputChange] =
    useForm({ name: '' });

  const { data: domainFolder } = useQuery({
    queryKey: ['folders', folderId],
    queryFn: async () => {
      const response = await axios.get(`/dashboards/folders/${folderId}`);
      return response.data;
    },
    onError: breadBoard.addInedibleToast,
  });

  useEffect(() => {
    if (domainFolder?.data.attributes.name) {
      setCurrentFolder({
        ...currentFolder,
        name: domainFolder.data.attributes.name,
      });
    }
  }, [domainFolder?.data.attributes.name]);

  if (!domainFolder) return null;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            Cancel
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              currentFolder.name.length === 0 ||
              currentFolder.name === domainFolder.data.attributes.name ||
              isConfirmDisabled
            }
            onClick={() => {
              onConfirm({
                folderParams: currentFolder,
                id: domainFolder.data.id,
              });
            }}
          >
            Save changes
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={onClose}
    >
      <>
        <div className='modalbox-header tw-border-grey-100'>
          <h2 className='modalbox-header__title truncated-text-container tw-text-l tw-font-semibold tw-text-grey-900'>
            Rename {domainFolder.data.attributes.name}
          </h2>
        </div>
        <div className='modalbox-body'>
          <ErrorMessage
            isFallback={false}
            modifiers='tw-mb-3'
            validationErrors={requestError.validationErrors}
          />
          <TextField
            autoFocus={true}
            fieldError={requestError.validationErrors.name}
            inputClassName='tw-px-3 tw-py-[9px]'
            isRequired={true}
            label='Folder name'
            name='name'
            onChange={handleCurrentFolderInputChange}
            removeErrorStyling={onInputFocus}
            value={currentFolder.name}
          />
        </div>
      </>
    </ModalBox>
  );
}

RenameFolderModal.propTypes = {
  isConfirmDisabled: PropTypes.bool,
  folderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onInputFocus: PropTypes.func.isRequired,
  requestError: PropTypes.shape({
    isFallback: PropTypes.bool.isRequired,
    validationErrors: PropTypes.object.isRequired,
  }),
};
